<script setup lang="ts">
import { useQuery } from "@tanstack/vue-query";
import type { ProductDetails } from "mws-ts-v2-sdk";

import { queries } from "~/queries";
import type { BidAttemptType } from "~/types/Catalog";

type Emits = {
  (e: "continue", tokenId?: string): void;
  (e: "close"): void;
};

defineEmits<Emits>();

const route = useRoute();
const { addNewCardClicked } = useAnalytics();

const { product, bidType, amountInActiveCurrency } = defineProps<{
  product: ProductDetails;
  amountInActiveCurrency: number;
  bidType?: BidAttemptType;
}>();

const bidCosts = useBidCosts({ product: () => product });
const reservationRequiredThreshold = computed(
  () => bidCosts.data.value?.preAuthorisationResponse?.threshold,
);
const reservationAmount = computed(
  () => bidCosts.data.value?.preAuthorisationResponse?.reservedAmount,
);

const {
  data: paymentTokens,
  isLoading,
  isSuccess,
} = useQuery(queries.tokens.list());

const selectedPaymentTokenId = ref<string>();
const selectedPaymentToken = computed(() =>
  paymentTokens.value?.results.find(
    (token) => token.id === selectedPaymentTokenId.value,
  ),
);

watch(
  isSuccess,
  () => {
    if (isSuccess && paymentTokens.value)
      selectedPaymentTokenId.value = paymentTokens.value.results[0]?.id;
  },
  { immediate: true },
);

const isAdyenDropinOpen = ref(false);

const localePath = useLocalePath();
const redirectPath = computed(() => {
  const redirectData = {
    modal: "reservationRequired",
    bidAmount: amountInActiveCurrency,
    bidType: bidType,
  };

  return localePath({
    path: route.path,
    query: {
      redirectData: JSON.stringify(redirectData),
    },
  });
});

const filteredPaymentTokens = computed(() => {
  const tokens = paymentTokens.value?.results || [];

  return tokens.filter((token) => token.isTokenized);
});

const handleAddCardClick = () => {
  addNewCardClicked({
    source: "bidding",
    number_active_cards: filteredPaymentTokens.value.length,
  });
  isAdyenDropinOpen.value = true;
};
</script>

<template>
  <BaseDialog
    :model-value="true"
    size="md"
    data-cy="bid-pre-authorization-modal"
    @close="$emit('close')"
  >
    <BaseDialogContent
      :fixed-height="true"
      data-cy="bid-pre-authorization-modal-content"
    >
      <template #title>
        <button
          v-if="isAdyenDropinOpen && paymentTokens?.resultCount !== 0"
          @click="isAdyenDropinOpen = false"
        >
          <BaseIcon
            name="arrowLeft"
            :size="20"
          />
        </button>

        {{ $t("product.cardReservationMessage.title") }}
      </template>
      <template #content>
        <I18nT
          v-if="
            reservationAmount?.currency &&
            reservationRequiredThreshold?.currency
          "
          scope="global"
          keypath="product.cardReservationMessage.description"
          tag="p"
          class="mb-5"
        >
          <template #PLACEHOLDER_threshold>
            <BaseCurrency
              :value="reservationRequiredThreshold.price"
              :iso="reservationRequiredThreshold.currency"
            />
          </template>
          <template #PLACEHOLDER_amount>
            <BaseCurrency
              :value="reservationAmount.price"
              :iso="reservationAmount.currency"
            />
          </template>
        </I18nT>
        <UserPaymentTokenCardTokenisation
          v-if="isAdyenDropinOpen || paymentTokens?.resultCount === 0"
          :redirect-path="redirectPath"
          @tokenisation-success="isAdyenDropinOpen = false"
        />
        <div
          v-else
          class="flex flex-col gap-4"
        >
          <p class="uppercase text-lg font-bold">
            {{ $t("product.cardReservationMessage.selectCard") }}
          </p>
          <BaseSkeletonLoader v-if="isLoading" />

          <UserPaymentTokenList
            v-else-if="paymentTokens?.results"
            :tokens="paymentTokens?.results"
            @card-selected="(token) => (selectedPaymentToken = token)"
          />
          <button
            data-cy="add-new-card-button"
            class="uppercase flex gap-1 mt-2"
            @click="handleAddCardClick"
          >
            <BaseIcon
              name="plus"
              :size="20"
              class="pr-2"
            />
            <p>{{ $t("myAccount.payment.addCard") }}</p>
          </button>
          <p class="text-platform-text-3 text-xs">
            {{ $t("product.cardReservationMessage.securePayment") }}
          </p>
        </div>
      </template>
      <template
        v-if="!isAdyenDropinOpen"
        #buttons
      >
        <BaseButton
          :disabled="!selectedPaymentToken || !selectedPaymentToken.isTokenized"
          @click="$emit('continue', selectedPaymentTokenId)"
        >
          {{ $t("product.cardReservationMessage.selectCardAndContinue") }}
        </BaseButton>
      </template>
    </BaseDialogContent>
  </BaseDialog>
</template>
